import { makeAutoObservable, runInAction } from "mobx";
import { track, type AnalyticsSource } from "@/lib/analytics";
import { changedFields } from "@/lib/changed-fields";
import type { RootStore } from "./root.store";
import type { IWorkspace } from "@/types/workspace";
import type {
  IWorkspaceDateRealignResult,
  IWorkspaceResetResult,
} from "@/services/workspace.service";
import {
  hasWorkspacePermission,
  isWorkspaceAdmin,
  type TWorkspacePermission,
} from "@/lib/permissions/workspace-permissions";

export class WorkspaceStore {
  workspaces: IWorkspace[] = [];
  isLoading = false;

  private rootStore: RootStore;
  // Dedupes concurrent fetchWorkspaces calls — multiple route loaders can
  // race for the same data on initial nav.
  private fetchWorkspacesPromise: Promise<IWorkspace[]> | null = null;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeAutoObservable(
      this,
      {
        rootStore: false,
        fetchWorkspacesPromise: false,
      } as never,
      { autoBind: true },
    );
  }

  /** Look up a workspace by its slug. Safe to call from route loaders —
   *  unlike `currentWorkspace`, this doesn't depend on router-store state
   *  that's populated by a React effect after components mount. */
  findBySlug(slug: string | undefined): IWorkspace | undefined {
    if (!slug) return undefined;
    return this.workspaces.find((ws) => ws.slug === slug);
  }

  get currentWorkspace(): IWorkspace | undefined {
    return this.findBySlug(this.rootStore.router.workspaceSlug);
  }

  get sortedWorkspaces(): IWorkspace[] {
    const current = this.currentWorkspace;
    const rest = this.workspaces
      .filter((ws) => ws.id !== current?.id)
      .sort((a, b) => a.name.localeCompare(b.name));
    return current ? [current, ...rest] : rest;
  }

  get permissions(): TWorkspacePermission[] {
    return (this.currentWorkspace?.permissions as TWorkspacePermission[]) ?? [];
  }

  hasPermission(permission: TWorkspacePermission): boolean {
    return hasWorkspacePermission(this.currentWorkspace, permission);
  }

  /** Whether the current user is a workspace admin (vs power/standard user).
   *  Gates admin-only surfaces such as the Form Workflow Builder (PRD §5.1). */
  get isAdmin(): boolean {
    return isWorkspaceAdmin(this.currentWorkspace);
  }

  async fetchWorkspaces(): Promise<IWorkspace[]> {
    if (this.fetchWorkspacesPromise) return this.fetchWorkspacesPromise;
    this.isLoading = true;
    const pending = (async () => {
      try {
        const workspaces = await this.rootStore.workspaceService.listWorkspaces();
        runInAction(() => {
          this.workspaces = workspaces;
        });
        return workspaces;
      } finally {
        runInAction(() => {
          this.isLoading = false;
          this.fetchWorkspacesPromise = null;
        });
      }
    })();
    this.fetchWorkspacesPromise = pending;
    return pending;
  }

  async createWorkspace(
    data: Partial<IWorkspace>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspace> {
    const workspace = await this.rootStore.workspaceService.createWorkspace(data);
    runInAction(() => {
      this.workspaces.push(workspace);
    });
    track("workspace_created", { workspace_id: workspace.id, source });
    return workspace;
  }

  async updateWorkspace(
    slug: string,
    data: Partial<IWorkspace>,
    source: AnalyticsSource = "page",
  ): Promise<IWorkspace> {
    // Read before the write: `fields` is what this save changed (NG-2064).
    const fields = changedFields(data, this.workspaceBySlug(slug));
    const workspace = await this.rootStore.workspaceService.updateWorkspace(slug, data);
    runInAction(() => {
      this.workspaces = this.workspaces.map((item) =>
        item.id === workspace.id ? workspace : item,
      );
    });
    track("workspace_updated", { workspace_id: workspace.id, fields, source });
    return workspace;
  }

  private workspaceBySlug(slug: string): IWorkspace | undefined {
    return this.workspaces.find((item) => item.slug === slug);
  }

  /**
   * Turn the FR20 SSO password-login override on or off for a workspace
   * (NG-1371). Admin-only server-side.
   *
   * Replacing the stored record is the whole mechanism: the response is the
   * whole workspace, so every reader
   * of `sso_password_login_override_enabled` reacts immediately — no reload.
   *
   * Errors propagate so the caller can surface a toast and leave the switch
   * showing the unchanged server state.
   */
  async setSsoOverrideEnabled(slug: string, enabled: boolean): Promise<IWorkspace> {
    const workspace = await this.rootStore.workspaceService.setSsoOverrideEnabled(slug, enabled);
    runInAction(() => {
      this.workspaces = this.workspaces.map((item) =>
        item.id === workspace.id ? workspace : item,
      );
    });
    track(
      enabled ? "sso_password_login_override_enabled" : "sso_password_login_override_disabled",
      { workspace_id: workspace.id, source: "page" },
    );
    return workspace;
  }

  /**
   * Delete a workspace (soft on the server — the slug is released) and drop it
   * from the local list so the switcher stops offering it. Only ever callable for
   * a demo workspace: the server 400s a sales delete otherwise and omits
   * `workspace.delete` from that workspace's `permissions[]`, so the UI never
   * offers the control. Errors propagate so the caller can surface the backend
   * message.
   */
  async deleteWorkspace(slug: string): Promise<void> {
    const workspaceId = this.workspaceBySlug(slug)?.id;
    await this.rootStore.workspaceService.deleteWorkspace(slug);
    track("workspace_deleted", { workspace_id: workspaceId, source: "page" });
    runInAction(() => {
      this.workspaces = this.workspaces.filter((item) => item.slug !== slug);
    });
  }

  /**
   * Reset a demo workspace to its canonical seeded state, then refetch the
   * workspace so the UI reflects the rebuilt data. The service runs the reset as
   * an async Celery job (POST -> 202 -> poll), so this resolves only once the
   * job finishes (which can take a few minutes). Returns the reset summary
   * (before/after row counts) for the caller to surface. Errors propagate so the
   * caller can show the appropriate message — the 400 "not a demo workspace"
   * Axios error, a task FAILURE, or a polling timeout. Pass `templateId` to
   * rebuild from a chosen workspace template instead of the canonical default,
   * and `version` to target a past retained version of that template (omitted ⇒
   * the template's active version).
   */
  async resetWorkspace(
    slug: string,
    { templateId, version }: { templateId?: string; version?: number } = {},
  ): Promise<IWorkspaceResetResult> {
    const summary = await this.rootStore.workspaceService.resetWorkspace(slug, {
      templateId,
      // Only thread `version` when chosen so callers that pass no version keep
      // the existing `{ templateId }`-only request shape (and assertions).
      ...(version !== undefined ? { version } : {}),
    });
    // Before the re-read, so a failed refetch can't drop it.
    track("workspace_reset", {
      workspace_id: this.workspaceBySlug(slug)?.id,
      template_id: templateId ?? null,
      past_version: version !== undefined,
      source: "page",
    });
    const workspace = await this.rootStore.workspaceService.getWorkspace(slug);
    runInAction(() => {
      this.workspaces = this.workspaces.map((item) =>
        item.id === workspace.id ? workspace : item,
      );
    });
    return summary;
  }

  /**
   * Move the workspace's content dates onto today, keeping its content (NG-961).
   *
   * The non-destructive sibling of {@link resetWorkspace}: nothing is wiped, so
   * everything added since the last reset survives and moves with the rest of the
   * timeline. Async + polled like the reset; resolves with the summary once the
   * job lands, rejects on an Axios error, a task FAILURE, or a polling timeout.
   *
   * Re-reads the workspace afterwards so `date_realignment` in the cache reflects
   * the new anchor — otherwise the button would keep offering the shift it just
   * applied.
   */
  async realignWorkspaceDates(slug: string): Promise<IWorkspaceDateRealignResult> {
    const summary = await this.rootStore.workspaceService.realignWorkspaceDates(slug);
    track("workspace_dates_realigned", {
      workspace_id: this.workspaceBySlug(slug)?.id,
      source: "page",
    });
    const workspace = await this.rootStore.workspaceService.getWorkspace(slug);
    runInAction(() => {
      this.workspaces = this.workspaces.map((item) =>
        item.id === workspace.id ? workspace : item,
      );
    });
    return summary;
  }

  async uploadLogo(slug: string, workspaceId: string, file: File): Promise<IWorkspace> {
    await this.rootStore.workspaceAssetService.uploadWorkspaceLogo(slug, workspaceId, file);
    track("workspace_logo_uploaded", { workspace_id: workspaceId, source: "page" });
    const workspace = await this.rootStore.workspaceService.getWorkspace(slug);
    runInAction(() => {
      this.workspaces = this.workspaces.map((item) =>
        item.id === workspace.id ? workspace : item,
      );
    });
    return workspace;
  }

  async removeLogo(slug: string, assetId: string): Promise<IWorkspace> {
    await this.rootStore.workspaceAssetService.deleteAsset(slug, assetId);
    track("workspace_logo_removed", {
      workspace_id: this.workspaceBySlug(slug)?.id,
      source: "page",
    });
    const workspace = await this.rootStore.workspaceService.getWorkspace(slug);
    runInAction(() => {
      this.workspaces = this.workspaces.map((item) =>
        item.id === workspace.id ? workspace : item,
      );
    });
    return workspace;
  }
}
